import axios from 'axios';

// API URL-i avtomatik təyin edir
const getApiBaseUrl = (): string => {
  // Əgər .env-də REACT_APP_API_BASE_URL varsa, onu istifadə et
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // Əks halda, frontend-in host-undan backend URL-ini yarat
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  
  // Development-də port 8000, production-da port yoxdur
  const isDevelopment = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.');
  const port = isDevelopment ? ':8000' : '';
  
  return `${protocol}//${hostname}${port}/api`;
};

export const API_BASE_URL = getApiBaseUrl();

console.log('🌐 CRM API Base URL:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
export const tokenManager = {
  getToken: () => localStorage.getItem('access_token'),
  getRefreshToken: () => localStorage.getItem('refresh_token'),
  setTokens: (accessToken: string, refreshToken: string) => {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
  },
  clearTokens: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('remember_me');
    localStorage.removeItem('token_expiry');
  },
  isRememberMeEnabled: () => {
    return localStorage.getItem('remember_me') === 'true';
  },
  shouldKeepLoggedIn: () => {
    const rememberMe = localStorage.getItem('remember_me') === 'true';
    const expiryStr = localStorage.getItem('token_expiry');
    
    if (!rememberMe || !expiryStr) {
      return false;
    }
    
    const expiry = new Date(expiryStr);
    const now = new Date();
    
    return now < expiry;
  }
};

// Request interceptor - hər sorğuya token əlavə et
api.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - token refresh və xətaları idarə et
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = tokenManager.getRefreshToken();
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
            refresh: refreshToken
          });
          
          const { access, refresh } = response.data;
          tokenManager.setTokens(access, refresh || refreshToken);
          
          // Orijinal sorğunu yenidən göndər
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh token da işləmirsə, logout et
          tokenManager.clearTokens();
          window.location.reload();
        }
      }
    }
    
    console.error('API Error:', error.response?.status, error.config?.url, error.message);
    return Promise.reject(error);
  }
);

export interface LoginData {
  email: string;
  password: string;
  remember?: boolean;
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_crm_user: boolean;
  is_superuser: boolean;
  profile_image?: string;
  current_company?: {
    id: number;
    name: string;
    slug: string;
  };
  companies?: Array<{
    id: number;
    name: string;
    slug: string;
    company_type: string;
    status: string;
    city: string;
    full_name: string;
    is_branch: boolean;
    role: string;
    permissions: {
      can_manage_students: boolean;
      can_view_reports: boolean;
      can_manage_users: boolean;
    };
    student_count: number;
  }>;
}

export interface LoginResponse {
  success: boolean;
  message?: string;
  user?: User;
  access?: string;
  refresh?: string;
  errors?: any;
}

export const authAPI = {
  login: async (data: LoginData): Promise<LoginResponse> => {
    try {
      const response = await api.post('/auth/jwt/login/', data);
      
      // JWT tokenləri saxla
      if (response.data.success && response.data.access && response.data.refresh) {
        tokenManager.setTokens(response.data.access, response.data.refresh);
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Login xətası:', error);
      return {
        success: false,
        errors: error.response?.data?.errors || { general: 'Bağlantı xətası' }
      };
    }
  },

  logout: async (): Promise<{ success: boolean; message?: string }> => {
    try {
      // JWT üçün logout endpoint-i yoxdur, sadəcə tokenləri təmizlə
      tokenManager.clearTokens();
      
      return { success: true, message: 'Uğurla çıxış etdiniz' };
    } catch (error) {
      console.error('Logout xətası:', error);
      // Xəta olsa belə tokenləri təmizlə
      tokenManager.clearTokens();
      return { success: false, message: 'Çıxış xətası' };
    }
  },

  getProfile: async (): Promise<{ success: boolean; user?: User; message?: string }> => {
    try {
      const response = await api.get('/auth/profile/');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 403 || error.response?.status === 401) {
        return { success: false, message: 'İstifadəçi daxil olmayıb' };
      }
      return { success: false, message: 'İstifadəçi məlumatları alınmadı' };
    }
  },

  // Token mövcudluğunu yoxla
  isAuthenticated: (): boolean => {
    return !!tokenManager.getToken();
  },

  switchCompany: async (companyId: number): Promise<{ success: boolean; message?: string; current_company?: any }> => {
    try {
      const response = await api.post('/auth/switch-company/', { company_id: companyId });
      return response.data;
    } catch (error: any) {
      console.error('Company switch xətası:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Şirkət dəyişdirilmədi'
      };
    }
  },

  // Şifrə reset tələbi
  requestPasswordReset: async (email: string): Promise<{ success: boolean; message?: string; errors?: any }> => {
    try {
      const response = await api.post('/auth/password-reset/', { email });
      return response.data;
    } catch (error: any) {
      console.error('Password reset xətası:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Şifrə reset tələbi göndərilmədi',
        errors: error.response?.data?.errors
      };
    }
  },

  // Şifrə reset təsdiqi
  confirmPasswordReset: async (uid: string, token: string, newPassword: string, confirmPassword: string): Promise<{ success: boolean; message?: string; errors?: any }> => {
    try {
      const response = await api.post('/auth/password-reset-confirm/', {
        uid,
        token,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      return response.data;
    } catch (error: any) {
      console.error('Password reset confirm xətası:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Şifrə dəyişdirilmədi',
        errors: error.response?.data?.errors
      };
    }
  },

  // Birbaşa şifrə dəyişdirmə (email təsdiqi ilə)
  directPasswordReset: async (email: string, newPassword: string, confirmPassword: string): Promise<{ success: boolean; message?: string; errors?: any }> => {
    try {
      const response = await api.post('/auth/direct-password-reset/', {
        email,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      return response.data;
    } catch (error: any) {
      console.error('Direct password reset xətası:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Şifrə dəyişdirilmədi',
        errors: error.response?.data?.errors
      };
    }
  },

  // Qeydiyyat
  register: async (data: {
    first_name: string;
    last_name: string;
    email: string;
    username: string;
    password: string;
    password_confirm: string;
  }): Promise<LoginResponse> => {
    try {
      const response = await api.post('/auth/register/', data);
      
      // JWT tokenləri saxla
      if (response.data.success && response.data.access && response.data.refresh) {
        tokenManager.setTokens(response.data.access, response.data.refresh);
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Register xətası:', error);
      return {
        success: false,
        errors: error.response?.data?.errors || { general: 'Qeydiyyat xətası' }
      };
    }
  },

  // Profil şəkli yükləmə
  uploadProfileImage: async (file: File): Promise<{ success: boolean; message?: string; profile_image?: string; errors?: any }> => {
    try {
      const formData = new FormData();
      formData.append('profile_image', file);
      
      const response = await api.post('/auth/upload-profile-image/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Profile image upload xətası:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Şəkil yüklənmədi',
        errors: error.response?.data?.errors
      };
    }
  },

  // Profil məlumatlarını yenilə
  updateProfile: async (data: {
    first_name: string;
    last_name: string;
    email: string;
  }): Promise<{ success: boolean; message?: string; user?: User; errors?: any }> => {
    try {
      const response = await api.put('/auth/profile/', data);
      return response.data;
    } catch (error: any) {
      console.error('Profile update xətası:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Profil yenilənmədi',
        errors: error.response?.data?.errors
      };
    }
  },

  // WhatsApp test mesajı göndər
  sendWhatsAppTest: async (message: string, toNumber?: string): Promise<{ success: boolean; message?: string; test_mode?: boolean; error?: string }> => {
    try {
      const response = await api.post('/auth/whatsapp-test/', {
        message,
        to_number: toNumber || '+994556772714'
      });
      return response.data;
    } catch (error: any) {
      console.error('WhatsApp test xətası:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'WhatsApp mesajı göndərilmədi',
        error: error.response?.data?.error || 'Naməlum xəta'
      };
    }
  },

  // SMS test mesajı göndər
  sendSMSTest: async (message: string, toNumber?: string): Promise<{ success: boolean; message?: string; phone?: string; sms_count?: number; error?: string }> => {
    try {
      const response = await api.post('/auth/sms-test/', {
        message,
        to_number: toNumber || '+994556772714'
      });
      return response.data;
    } catch (error: any) {
      console.error('SMS test xətası:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'SMS göndərilmədi',
        error: error.response?.data?.error || 'Naməlum xəta'
      };
    }
  },
};

export default api;